import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Modal } from "react-bootstrap";
import Cropper from "react-easy-crop";
import { v4 as uuidv4 } from "uuid";
import getCroppedImg from "./CropImageHelper";

export default function ImageCropModal({
  show,
  onHide,
  originalFile,
  maxWidth, // örn. 800
  maxHeight, // örn. 600
  onComplete,
}) {
  /* ---------- STATE ---------- */
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRot] = useState(0);
  const [minZoom, setMinZoom] = useState(1);
  const [maxZoom, setMaxZoom] = useState(3);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [areaPx, setAreaPx] = useState(null);

  /* ---------- KAYNAK ---------- */
  const imageSrc = useMemo(() => {
    if (!originalFile) return null;
    return typeof originalFile === "string"
      ? originalFile
      : URL.createObjectURL(originalFile);
  }, [originalFile]);

  // ⬇︎ yeni state
  const [fitMode, setFitMode] = useState("contain");

  /* ---------- SABİT IZGARA ---------- */
  const cw = Number(maxWidth) || 0;
  const ch = Number(maxHeight) || 0;
  const aspect = cw && ch ? cw / ch : 1;

  /* ---------- FIT ZOOM HESABI ---------- */
  const computeFit = useCallback(
    (rot) => {
      if (!natural.w) return 1;
      const swap = rot % 180 !== 0;
      const imgW = swap ? natural.h : natural.w;
      const imgH = swap ? natural.w : natural.h;

      // “cover” ölçeği
      return Math.max(cw / imgW, ch / imgH);
    },
    [natural, cw, ch],
  );

  /* ---------- ölçek değiştiğinde ---------- */
  useEffect(() => {
    const fit = computeFit(rotation);

    setCrop({ x: 0, y: 0 });

    // >>> ölçek 1'den küçükse aynen kullan
    setZoom(fit); // başlangıç zoom’u
    setMinZoom(fit); // en geri zoom buna kadar
    setMaxZoom(fit + 2); // ileri zoom limiti
  }, [natural, rotation, computeFit]);

  useEffect(() => {
    setAreaPx(null);
  }, [imageSrc, cw, ch, rotation]);

  const onMediaLoaded = ({ naturalWidth, naturalHeight }) =>
    setNatural({ w: naturalWidth, h: naturalHeight });

  /* ---------- ROTATE BUTTONS ---------- */
  const rotL = () => setRot((r) => (r - 90 + 360) % 360);
  const rotR = () => setRot((r) => (r + 90) % 360);

  /* ---------- CROP COMPLETE ---------- */
  const onDone = useCallback((_, pixels) => setAreaPx(pixels), []);

  const apply = async () => {
    if (!imageSrc || !areaPx || !cw || !ch) return; // cw/ch 0 ise kırpma yok
    const fileName = `cropped-${uuidv4()}.webp`;
    const file = await getCroppedImg(
      imageSrc,
      areaPx,
      rotation,
      fileName,
      cw,
      ch,
      "image/webp",
    );
    onComplete(file);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} fullscreen centered scrollable>
      <Modal.Header closeButton>
        <Modal.Title>Resmi Kırp & Düzenle</Modal.Title>
      </Modal.Header>

      <Modal.Body className="d-flex justify-content-center align-items-center">
        {imageSrc && (
          <div
            style={{
              width: cw,
              height: ch,
              position: "relative",
              overflow: "visible",
            }}
          >
            <Cropper
              key={rotation}
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              minZoom={minZoom}
              objectFit="fitMode"
              restrictPosition
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRot}
              onCropComplete={onDone}
              onMediaLoaded={onMediaLoaded}
              style={{
                // ➊ Croper’ın kendi konteynerini taşmaya aç
                containerStyle: { overflow: "visible" },

                // ➋ Crop alanı artık overflow:hidden değil
                cropAreaStyle: {
                  border: "2px solid #10B5FF",
                  overflow: "visible",
                },

                // ➌ Medya da taşsın
                mediaStyle: {
                  overflow: "visible",
                },

                // maskStyle olduğu gibi kalsın
                maskStyle: {
                  background: "rgba(0,0,0,0.5)",
                  backdropFilter: "blur(4px)",
                },
              }}
              // cropSize sabit cw/ch
              cropSize={{ width: cw, height: ch }}
            />
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="d-flex flex-column">
        <div className="d-flex gap-2 my-2">
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={rotL}
          >
            ⟲
          </button>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={rotR}
          >
            ⟲
          </button>
        </div>

        <input
          type="range"
          min={minZoom}
          max={maxZoom}
          step={0.1}
          value={zoom}
          onChange={(e) => setZoom(+e.target.value)}
          style={{ width: 220 }}
        />

        <div className="d-flex justify-content-between w-100 mt-3">
          <button type="button" className="btn btn-secondary" onClick={onHide}>
            İptal
          </button>
          <button type="button" className="btn btn-primary" onClick={apply}>
            Uygula
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
